import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

const SORT_FIELDS = {
  name: 'text', industry: 'text', owner_name: 'text', city: 'text',
  contact_count: 'number', open_deals: 'number', pipeline_value: 'number',
};

export default function AccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [users, setUsers] = useState([]);
  const [uniqueNames, setUniqueNames] = useState([]);
  const [uniqueCities, setUniqueCities] = useState([]);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [form, setForm] = useState({
    name:'', industry:'', website:'', owner_id:'',
    address_line1:'', address_line2:'', city:'', state:'', zip:'', country:'USA'
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = (q = '') => {
    setLoading(true);
    api.get(`/accounts?search=${encodeURIComponent(q)}&limit=200`)
      .then(r => {
        setAccounts(r.data.accounts);
        setTotal(r.data.total);
        // Build unique name and city lists for filter dropdowns
        const names = [...new Set(r.data.accounts.map(a => a.name))].sort();
        const cities = [...new Set(r.data.accounts.map(a => a.city).filter(Boolean))].sort();
        setUniqueNames(names);
        setUniqueCities(cities);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api.get('/users').then(r => setUsers(r.data.users || [])).catch(() => {});
  }, []);

  const handleSearch = e => {
    setSearch(e.target.value);
    clearTimeout(window._ast);
    window._ast = setTimeout(() => load(e.target.value), 350);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const clearFilters = () => {
    setFilterName('');
    setFilterCity('');
    setFilterOwner('');
  };

  const activeFilterCount = [filterName, filterCity, filterOwner].filter(Boolean).length;

  // Apply filters client-side
  const filtered = accounts.filter(a => {
    if (filterName && a.name !== filterName) return false;
    if (filterCity && a.city !== filterCity) return false;
    if (filterOwner && a.owner_id !== filterOwner) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const type = SORT_FIELDS[sortField] || 'text';
    let valA = a[sortField];
    let valB = b[sortField];
    if (type === 'number') {
      valA = parseFloat(valA || 0);
      valB = parseFloat(valB || 0);
      return sortDir === 'asc' ? valA - valB : valB - valA;
    } else {
      valA = (valA || '').toLowerCase();
      valB = (valB || '').toLowerCase();
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    }
  });

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Account name is required.');
    setSaving(true);
    try {
      await api.post('/accounts', { ...form, owner_id: form.owner_id || null });
      setShowForm(false);
      setForm({ name:'', industry:'', website:'', owner_id:'', address_line1:'', address_line2:'', city:'', state:'', zip:'', country:'USA' });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save account.');
    } finally {
      setSaving(false);
    }
  };

  const fmt = n => Number(n || 0).toLocaleString('en-US', { style:'currency', currency:'USD', maximumFractionDigits:0 });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span style={{ color:'var(--border-md)', marginLeft:4 }}>↕</span>;
    return <span style={{ color:'var(--accent)', marginLeft:4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const SortTh = ({ field, children }) => (
    <th onClick={() => handleSort(field)}
      style={{ cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}>
      {children}<SortIcon field={field} />
    </th>
  );

  return (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ fontSize:13, color:'var(--text-secondary)' }}>
            {filtered.length} of {total} accounts
          </div>
          {activeFilterCount > 0 && (
            <span className="badge badge-blue">{activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active</span>
          )}
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <input
            value={search} onChange={handleSearch} placeholder="Search accounts…"
            style={{ height:32, padding:'0 10px', border:'0.5px solid var(--border-md)',
                     borderRadius:'var(--radius)', background:'var(--bg-secondary)',
                     color:'var(--text)', fontSize:13, outline:'none', width:200 }}
          />
          <button className="btn" onClick={() => setShowFilters(!showFilters)}
            style={{ position:'relative' }}>
            ⚙ Filter {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
          </button>
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Add account</button>
        </div>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="card" style={{ marginBottom:12, padding:14 }}>
          <div style={{ display:'flex', gap:12, alignItems:'flex-end', flexWrap:'wrap' }}>
            <div>
              <label className="form-label">Filter by account name</label>
              <select className="form-input" value={filterName}
                onChange={e => setFilterName(e.target.value)}
                style={{ width:220 }}>
                <option value="">— All accounts —</option>
                {uniqueNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Filter by city</label>
              <select className="form-input" value={filterCity}
                onChange={e => setFilterCity(e.target.value)}
                style={{ width:180 }}>
                <option value="">— All cities —</option>
                {uniqueCities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Filter by owner</label>
              <select className="form-input" value={filterOwner}
                onChange={e => setFilterOwner(e.target.value)}
                style={{ width:180 }}>
                <option value="">— All owners —</option>
                <option value="unassigned">Unassigned</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            {activeFilterCount > 0 && (
              <button className="btn" onClick={clearFilters} style={{ marginBottom:1 }}>
                Clear filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Add Account Form */}
      {showForm && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="section-title" style={{ marginBottom:14 }}>New account</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label className="form-label">Account name *</label>
              <input className="form-input" value={form.name}
                onChange={e => setForm({...form, name:e.target.value})}
                placeholder="e.g. Harwin Inc." />
            </div>
            <div>
  <label className="form-label">Industry</label>
  <select className="form-input" value={form.industry}
    onChange={e => setForm({...form, industry:e.target.value})}>
    <option value="">— Select industry —</option>
    <option>Aerospace & Defense</option>
    <option>Automotive</option>
    <option>Communications</option>
    <option>Consumer</option>
    <option>Gaming</option>
    <option>Industrial</option>
    <option>Lighting</option>
    <option>Medical</option>
    <option>Security</option>
    <option>Solar</option>
    <option>Space</option>
  </select>
</div>
            <div>
              <label className="form-label">Account owner</label>
              <select className="form-input" value={form.owner_id}
                onChange={e => setForm({...form, owner_id:e.target.value})}>
                <option value="">— Unassigned —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Website</label>
              <input className="form-input" value={form.website}
                onChange={e => setForm({...form, website:e.target.value})}
                placeholder="https://..." />
            </div>
            <div>
              <label className="form-label">Address line 1</label>
              <input className="form-input" value={form.address_line1}
                onChange={e => setForm({...form, address_line1:e.target.value})}
                placeholder="Street address" />
            </div>
            <div>
              <label className="form-label">Address line 2</label>
              <input className="form-input" value={form.address_line2}
                onChange={e => setForm({...form, address_line2:e.target.value})}
                placeholder="Suite, unit, etc." />
            </div>
            <div>
              <label className="form-label">City</label>
              <input className="form-input" value={form.city}
                onChange={e => setForm({...form, city:e.target.value})}
                placeholder="City" />
            </div>
            <div>
              <label className="form-label">State</label>
              <input className="form-input" value={form.state}
                onChange={e => setForm({...form, state:e.target.value})}
                placeholder="State" />
            </div>
            <div>
              <label className="form-label">ZIP code</label>
              <input className="form-input" value={form.zip}
                onChange={e => setForm({...form, zip:e.target.value})}
                placeholder="ZIP" />
            </div>
          </div>
          <div style={{ display:'flex', gap:8, marginTop:14 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save account'}
            </button>
            <button className="btn" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Accounts Table */}
      <div className="card" style={{ padding:0 }}>
        {loading
          ? <div style={{ padding:24, fontSize:13, color:'var(--text-tertiary)' }}>Loading…</div>
          : <table className="data-table">
              <thead>
                <tr>
                  <SortTh field="name">Account name</SortTh>
                  <SortTh field="industry">Industry</SortTh>
                  <SortTh field="owner_name">Owner</SortTh>
                  <SortTh field="city">Location</SortTh>
                  <SortTh field="contact_count">Contacts</SortTh>
                  <SortTh field="open_deals">Open deals</SortTh>
                  <SortTh field="pipeline_value">Pipeline</SortTh>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0
                  ? <tr><td colSpan={7} style={{ padding:24, color:'var(--text-tertiary)', textAlign:'center' }}>
                      No accounts match your filters.
                    </td></tr>
                  : sorted.map(a => (
                      <tr key={a.id} style={{ cursor:'pointer' }} onClick={() => navigate(`/accounts/${a.id}`)}>
                        <td>
                          <div style={{ fontWeight:500 }}>{a.name}</div>
                          {a.website && <div style={{ fontSize:11, color:'var(--accent-dark)' }}>{a.website}</div>}
                        </td>
                        <td style={{ color:'var(--text-secondary)' }}>{a.industry || '—'}</td>
                        <td>
                          {a.owner_name
                            ? <span className="badge badge-blue">{a.owner_name}</span>
                            : <span className="badge badge-gray">Unassigned</span>
                          }
                        </td>
                        <td style={{ color:'var(--text-secondary)', fontSize:12 }}>
                          {[a.city, a.state].filter(Boolean).join(', ') || '—'}
                        </td>
                        <td><span className="badge badge-gray">{a.contact_count}</span></td>
                        <td><span className="badge badge-blue">{a.open_deals}</span></td>
                        <td style={{ fontWeight:500, color:'var(--accent-dark)' }}>{fmt(a.pipeline_value)}</td>
                      </tr>
                    ))
                }
              </tbody>
            </table>
        }
      </div>
    </>
  );
}